import { Box, ButtonProps, chakra, FormatNumber, HStack, Image, Separator, Spinner, Text } from "@chakra-ui/react";
import { motion, useTime, useTransform } from "motion/react";
import { useRef } from "react";

import { NavButton, NavButtonDirection } from "../nav-button";
import { RoundedButton } from "../rounded-button";
import { PopoverArrow, PopoverBody, PopoverContent, PopoverRoot, PopoverTrigger } from "../ui/popover";

import { BUTTON_HEIGHT, BUTTON_VARIANT } from "./constants";

import { AppRoute } from "@/constants/shared";
import { useDailySpinOnchain } from "@/hooks/useDailySpinOnchain";

const MotionBox = chakra(motion.div);

export const SpinningButton = ({
  label,
  ml,
  ...props
}: { label?: string } & ButtonProps & { direction?: NavButtonDirection }) => {
  const time = useTime();
  const rotate = useTransform(time, [0, 3000], [0, 360], {
    clamp: false,
  });
  const rotatingBg = useTransform(rotate, (r) => {
    return `conic-gradient(from ${r}deg, #FFDB20, #FFA800, #FFDB20, #FFDB20, #FFDB20, #FFA800, #FFDB20, #FFDB20, #FFDB20)`;
  });

  return (
    <Box position="relative" display="inline-block" zIndex="1" ml={ml}>
      <NavButton
        bgColor="#FFF6C7"
        variant="ghost"
        _hover={{
          bg: "#ffde39",
          opacity: 0.8,
        }}
        h={38}
        href={AppRoute.DailySpin}
        position="relative"
        zIndex="1"
        {...props}
      >
        <Image boxSize={4} src="/assets/lightning-dark.svg" my={"2px"} />
        {label || "Daily Spin"}
      </NavButton>
      <MotionBox position="absolute" inset="-1px" borderRadius="full" zIndex="0" style={{ background: rotatingBg }} />
    </Box>
  );
};

export const DailySpinPopover = () => {
  const ref = useRef<HTMLDivElement | null>(null);
  const { hasSpunToday, dailySpinStreak, isLoadingDailySpinStats } = useDailySpinOnchain();
  if (isLoadingDailySpinStats) return <Spinner />;
  if (!hasSpunToday) {
    return <SpinningButton />;
  }
  return (
    <PopoverRoot initialFocusEl={() => ref.current}>
      <PopoverTrigger asChild>
        <RoundedButton variant={BUTTON_VARIANT} h={BUTTON_HEIGHT}>
          <Image boxSize={4} src="/assets/lightning.svg" my={"2px"} />
          <FormatNumber value={dailySpinStreak} />
        </RoundedButton>
      </PopoverTrigger>
      <PopoverContent ref={ref} h={221} w={300}>
        <PopoverArrow />
        <PopoverBody spaceY={2} justifyContent="space-between" flexDir="column" display="flex">
          <Text color="fg.muted" fontWeight="600" fontSize="sm">
            Daily Spin
          </Text>
          <HStack>
            <Image src="/assets/daily-spin/lightning-bolt-reward.svg" boxSize={6} />
            <Text fontWeight="600" fontSize="md">
              {dailySpinStreak} Day Streak
            </Text>
          </HStack>
          <Separator />
          <Text color="fg.muted" fontWeight="500" fontSize="xs">
            Spin the fortune wheel for a chance to win Plume Points, raffle tickets or other rewards! Keep your daily
            streak high to get bonus PP.
          </Text>
          <NavButton w="full" href="/daily-spin">
            Spin Now
          </NavButton>
        </PopoverBody>
      </PopoverContent>
    </PopoverRoot>
  );
};
